import { Autocomplete, Chip, FormControl, Tooltip,createFilterOptions } from "@mui/material";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { useMaterialUIController } from "context";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker } from "rsuite";
import moment from "moment-timezone";
import Statistics from "./TransactionStatistics";
import {
  getTransactionReport,
  getReservationReport,
  getValidationAndFreeParkingReport,
} from "store/slice/reports/reportSlice";
import CircularIndeterminate from "components/MDLoading";
import { get, isEmpty, uniqBy } from "lodash";
import * as XLSX from "xlsx";
import DownloadIcon from "@mui/icons-material/Download";
import { centsToDollars } from "global/functions";
import MDBadge from "components/MDBadge";
import MDSnackbar from "components/MDSnackbar";
import { getPlaces } from "store/slice/places/placeSlice";
import NoDataOverlay from "components/Common/NoDataOverlay";

function TransactionReports() {
  const [controller] = useMaterialUIController();
  const dispatch = useDispatch();
  const { sidenavColor } = controller;
  const allPlaces = useSelector((state) => state.places?.placesList);

  const [statistics, setStatistics] = useState({});
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0); // percent 0-100
  const loading = useSelector((state) => state.report?.loading);

  const [notification, setNotification] = useState({
    color: "",
    icon: "",
    title: "",
    content: "",
    show: false,
    close: () => setNotification({ ...notification, show: false }),
  });

  const currentDate = moment().endOf("day").toDate();
  const lastWeekDate = moment().subtract(7, "days").startOf("day").toDate();
  const timeZones = uniqBy(allPlaces, "timeZoneId").map(
    (place) => place?.timeZoneId || "",
  );
  timeZones.push("Asia/Kolkata");
  timeZones.push("UTC");
  const [filterOptions, setFilterOptions] = useState({
    tz: "UTC",
    status: "All",
    placeId: [allPlaces.length > 0 && allPlaces[0]?._id],
    startDate: moment(lastWeekDate),
    endDate: moment(currentDate),
    purpose: "All",
  });
  const asyncFn = async () => {
    await dispatch(getPlaces())
      .unwrap()
      .then(async (res) => {
        setFilterOptions((prev) => ({
          ...prev,
          placeId: [res.places[0]?._id],
        }));
        // handleGetReport();
      });
  };
  useEffect(() => {
    asyncFn();
  }, []);

  const handleStartDateChange = (value) => {
    setFilterOptions((prev) => ({
      ...prev,
      startDate: value ? moment(value).format() : null,
    }));
  };

  const handleEndDateChange = (value) => {
    setFilterOptions((prev) => ({
      ...prev,
      endDate: value ? moment(value).format() : null,
    }));
  };

  const handleGetReport = async () => {
    try {
      if (filterOptions.placeId.length === 0) {
        setNotification({
          ...notification,
          color: "error",
          title: "Error",
          content: "Please select at least 1 lot",
          icon: "warning",
          show: true,
        });
        return;
      }

      if (filterOptions.purpose === "All") {
        const purposes = ["PARKING", "SUBSCRIPTION"];
        const [resParking, resSubscription] = await Promise.all(
          purposes.map(async (p) => {
            const transPayload = {
              ...filterOptions,
              status: filterOptions.status.toLowerCase(),
              purpose: p,
              startDate: moment(filterOptions.startDate)
                .format("YYYY-MM-DD HH:mm")
                .toString(),
              endDate: moment(filterOptions.endDate)
                .format("YYYY-MM-DD HH:mm")
                .toString(),
              page: 1,
              limit: 0, // stats only
              statsOnly: true,
            };
            const transRes = await dispatch(getTransactionReport(transPayload)).unwrap();

            let reservationStats = {};
            if (transRes.success) {
              const resPayload = {
                ...filterOptions,
                status: filterOptions.status.toLowerCase(),
                purpose: p,
                startDate: moment(filterOptions.startDate)
                  .format("YYYY-MM-DD HH:mm:ss")
                  .toString(),
                endDate: moment(filterOptions.endDate)
                  .format("YYYY-MM-DD HH:mm:ss")
                  .toString(),
                page: 1,
                limit: 0, // stats only
                statsOnly: true,
              };
              const resRes = await dispatch(getReservationReport(resPayload)).unwrap();
              if (resRes.success) {
                reservationStats = resRes.reservationStatistics;
              }
            }
            return {
              success: transRes.success,
              transactionStatistics: transRes.transactionStatistics,
              reservationStatistics: reservationStats,
              totalTransactions: transRes.totalTransactions || 0,
            };
          })
        );

        if (resParking?.success || resSubscription?.success) {
          const parkingCombined = {
            ...(resParking?.transactionStatistics || {}),
            ...(resParking?.reservationStatistics || {}),
          };
          const subscriptionCombined = {
            ...(resSubscription?.transactionStatistics || {}),
            ...(resSubscription?.reservationStatistics || {}),
          };

          const combineStats = (stats1 = {}, stats2 = {}) => {
            const combined = {};
            const allKeys = new Set([
              ...Object.keys(stats1),
              ...Object.keys(stats2),
            ]);
            allKeys.forEach((key) => {
              const val1 = typeof stats1[key] === "number" ? stats1[key] : 0;
              const val2 = typeof stats2[key] === "number" ? stats2[key] : 0;
              combined[key] = val1 + val2;
            });
            return combined;
          };

          setStatistics({
            isAll: true,
            all: combineStats(parkingCombined, subscriptionCombined),
            parking: parkingCombined,
            subscription: subscriptionCombined,
          });
          setTotalTransactions(
            (resParking?.totalTransactions || 0) +
              (resSubscription?.totalTransactions || 0)
          );
        }
      } else {
        const payload = {
          ...filterOptions,
          status: filterOptions.status.toLowerCase(),
          purpose: filterOptions.purpose.toUpperCase(),
          startDate: moment(filterOptions.startDate)
            .format("YYYY-MM-DD HH:mm")
            .toString(),
          endDate: moment(filterOptions.endDate)
            .format("YYYY-MM-DD HH:mm")
            .toString(),
          page: 1,
          limit: 0, // stats only
          statsOnly: true,
        };
        const res = await dispatch(getTransactionReport(payload)).unwrap();

        if (res.success) {
          let stats = { ...res.transactionStatistics };
          const resPayload = {
            ...filterOptions,
            status: filterOptions.status.toLowerCase(),
            purpose: filterOptions.purpose.toUpperCase(),
            startDate: moment(filterOptions.startDate)
              .format("YYYY-MM-DD HH:mm:ss")
              .toString(),
            endDate: moment(filterOptions.endDate)
              .format("YYYY-MM-DD HH:mm:ss")
              .toString(),
            page: 1,
            limit: 0, // stats only
            statsOnly: true,
          };
          const resRes = await dispatch(getReservationReport(resPayload)).unwrap();
          if (resRes.success) {
            stats = {
              ...stats,
              ...resRes.reservationStatistics,
            };
          }
          setStatistics({
            isAll: false,
            ...stats,
          });
          setTotalTransactions(res.totalTransactions || 0);
        }
      }
    } catch (error) {
      console.log("error --->", error.message);
    }
  };

  const downloadReport = async () => {
    try {
      // if (totalTransactions === 0) return;
      // fetch in chunks to avoid large payloads
      const pageSize = 1000; // adjusted as requested
      const allData = [];
      let page = 1;
      setDownloading(true);
      setDownloadProgress(0);
      const pagePayload = {
        ...filterOptions,
        status: filterOptions.status.toLowerCase(),
        purpose: filterOptions.purpose.toUpperCase(),
        startDate: moment(filterOptions.startDate)
          .format("YYYY-MM-DD HH:mm")
          .toString(),
        endDate: moment(filterOptions.endDate)
          .format("YYYY-MM-DD HH:mm")
          .toString(),
        page,
        limit: pageSize,
        statsOnly: false,
      };
      const pageRes = await dispatch(
        getValidationAndFreeParkingReport(pagePayload),
      ).unwrap();
      const dataChunkValidation = get(pageRes, "reservations", []);
      allData.push(...dataChunkValidation);
      while (true) {
        const pagePayload = {
          ...filterOptions,
          status: filterOptions.status.toLowerCase(),
          purpose: filterOptions.purpose.toUpperCase(),
          startDate: moment(filterOptions.startDate)
            .format("YYYY-MM-DD HH:mm")
            .toString(),
          endDate: moment(filterOptions.endDate)
            .format("YYYY-MM-DD HH:mm")
            .toString(),
          page,
          limit: pageSize,
          statsOnly: false,
        };
        const pageRes = await dispatch(
          getTransactionReport(pagePayload),
        ).unwrap();
        const dataChunk = get(pageRes, "transactions", []);
        allData.push(...dataChunk);
        // update progress
        const fetched = Math.min(allData.length, totalTransactions);
        const pct = Math.round((fetched / totalTransactions) * 100);
        setDownloadProgress(pct);
        if (dataChunk.length < pageSize) break; // last page
        page += 1;
      }
      // proceed to export
      const data = allData;
      const columnMapping = {
        "Transaction ID": "transactionId",
        "Transaction Date": "transactionDate",
        "License Plate": "licensePlate",
        "Space Number": "spaceNumber",
        "Mobile Number": "mobile",
        Email: "email",
        Name: "fullName",
        "Base Rate": "baseRate",
        Tax: "tax",
        "City Tax": "cityTax",
        "County Tax": "countyTax",
        "Service Fee": "serviceFee",
        "Payment Gateway Fee": "paymentGatewayFee",
        "Owner Payout": "ownerPayout",
        "Client Payout": "primaryOwnerPayout",
        "Operator Payout": "secondaryOwnerPayout",
        "Dock Revenue": "dockRevenue",
        "Total Amount": "totalAmount",
        "Payment Gateway Fee Pay By": "paymentGatewayFeePayBy",
        "Payment Method": "paymentMethodType",
        Purpose: "purpose",
        id: "mongooseId",
        "Total Refund": "totalRefund",
        "Refund Transaction ID": "refundTransactionId",
        "Customer ID": "stripeCustomerId",
        "Payment Method ID": "paymentMethodId",
        "Payment Status": "paymentStatus",
        "Parking Code": "parkingCode",
        "Lot Address": "placeAddress",
        "Failed Reason": "failedReason",
        "Start Date": "startDate",
        "End Date": "endDate",
        Rate: "rate",
        "Extend Reminder Sent?": "isExtendReminderSend",
        "Extended Reservation": "isExtend",
        "Validation Code": "validationCode",
      };

      const filteredData = data.map((item) => {
        const filteredItem = {};
        Object.keys(columnMapping).forEach((columnName) => {
          const key = columnMapping[columnName];
          let value = item[key];
          if (key === "fullName") {
            value = item?.parkerName||"-";
          } else if (key === "transactionDate") {
            if (
               item?.paymentMethodType === "VALIDATION CODE" ||
              item?.paymentMethodType === "FREE PARKING"
            ) {
              value = value
                ? moment
      .utc(value)
      .tz(filterOptions.tz)
      .format("YYYY-MM-DD HH:mm:ss") 
  : "";
            } else {
              value = value
                ? moment
                    .utc(value)
                    .tz(filterOptions.tz)
                    .format("YYYY-MM-DD HH:mm:ss")
                : "";
            }
          } else if (key === "startDate" || key === "endDate") {
            value = value
              ? moment
                  .utc(value)
                  .tz(filterOptions.tz)
                  .format("YYYY-MM-DD HH:mm:ss")
              : "";
          } else if (key === "email") {
            value =
              item["customerId"] && item["customerId"]["email"]
                ? item["customerId"]["email"]
                : item["parkerEmail"] || "";
          } else if (key === "mobile") {
            value =
              item["customerId"] && item["customerId"]["mobile"]
                ? item["customerId"]["mobile"]
                : item["customerId"] && item["customerId"]["secondaryMobile"]
                  ? item["customerId"]["secondaryMobile"]
                  : "";
          } else if (
            [
              "baseRate",
              "tax",
              "cityTax",
              "countyTax",
              "serviceFee",
              "paymentGatewayFee",
              "dockRevenue",
              "ownerPayout",
              "totalAmount",
            ].includes(key)
          ) {
            value = value ? centsToDollars(value) : 0;
          } else if (key === "paymentMethodType") {
            value =
              value === "card"
                ? "Credit Card"
                : value === "ACH"
                  ? "ACH"
                  : value;
          } else if (key === "isMonthly") {
            value = value === true ? "Monthly" : "Custom";
          } else if (key === "isAutoRenew") {
            value = value === true ? "Yes" : "No";
          } else if (key === "placeAddress") {
            value = get(item, "placeId.google.formatted_address", "");
          } else if (key === "parkingCode") {
            value = get(item, "placeId.parkingCode", "");
          } else if (key === "mongooseId") {
            value =
              item.purpose === "PARKING"
                ? item.transientNumber
                : item.subscriptionNumber;
          } else if (key === "refundTransactionId") {
            value = item.paymentStatus === "refunded" ? item.transactionId : "";
          } else if (key === "totalRefund") {
            value =
              item.paymentStatus === "refunded"
                ? centsToDollars(item.totalAmount)
                : "";
          } else if (key === "failedReason") {
            value =
              item.paymentStatus === "failed" ? item.paymentInfo?.message : "";
          } else if (key === "primaryOwnerPayout") {
            value = centsToDollars(item?.primaryConnectAccountPayout) || 0;
          } else if (key === "secondaryOwnerPayout") {
            value = centsToDollars(item?.secondaryConnectAccountPayout) || 0;
          } else if (key === "licensePlate") {
            console.log("value >>>>>>>>>>>", value);
            const result = (value || [])
              .map((item) => {
                // Case 1: object with status & licensePlateNumber
                if (
                  typeof item === "object" &&
                  item !== null &&
                  item.status === 10 &&
                  item.licensePlateNumber
                ) {
                  return item.licensePlateNumber;
                }

                // Case 2: already a string
                if (typeof item === "string") {
                  return item;
                }

                return null;
              })
              .filter(Boolean)
              .join(", ");
            value = result;
          } else if (key === "rate") {
            value = get(item, "rateId.displayName", item?.rate);
          }

          filteredItem[columnName] = value;
        });
        return filteredItem;
      });

      const ws = XLSX.utils.json_to_sheet(filteredData);

      const columnWidths = Object.keys(columnMapping).map((columnName) => {
        switch (columnName) {
          case "Subscription ID":
            return { wch: 15 };
          case "Base Rate":
          case "Tax":
          case "City Tax":
          case "County Tax":
          case "Service Fee":
          case "Total Amount":
            return { wch: 10 };
          case "License Plate":
            return { wch: 30 };
          default:
            return { wch: 25 };
        }
      });
      ws["!cols"] = columnWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
      const safeFileName = `transaction-report_${moment().format(
        "YYYY-MM-DD_HH-mm",
      )}.xlsx`;
      XLSX.writeFile(wb, safeFileName);
      setNotification({
        ...notification,
        color: "success",
        title: "Download ready",
        content: safeFileName,
        icon: "check",
        show: true,
      });
      setDownloading(false);
      setDownloadProgress(0);
    } catch (e) {
      console.error("download error", e);
      setNotification({
        ...notification,
        color: "error",
        title: "Download failed",
        content: e.message || "Unable to generate report",
        icon: "error",
        show: true,
      });
      setDownloading(false);
      setDownloadProgress(0);
    }
  };

  const handlePlaceChange = (_, newValue) => {
    if (newValue.includes("All")) {
      setFilterOptions((prev) => ({
        ...prev,
        placeId: allPlaces.map((place) => place?._id),
      }));
    } else if (newValue.length === 0) {
      setFilterOptions((prev) => ({
        ...prev,
        placeId: [],
      }));
    } else {
      setFilterOptions((prev) => ({
        ...prev,
        placeId: newValue.map((place) => place?._id),
      }));
    }
  };

  const placeFilterOptions = createFilterOptions({
  stringify: (option) => {
    if (option === "All") return "All Places";

    return [
      option?.parkingCode,
      option?.google?.formatted_address,
      option?.timeZoneId,
    ]
      .filter(Boolean)
      .join(" ");
  },
});

  return (
    <>
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        open={notification.show}
        close={notification.close}
        bgWhite
      />
      <MDBox>
        <Grid container spacing={2}>
          {/* Row 1: Place + Timezone */}
          <Grid item xs={12} md={10}>
            <FormControl fullWidth>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={
                  allPlaces.length > 1 ? ["All", ...allPlaces] : allPlaces
                }
                filterOptions={placeFilterOptions}
                getOptionLabel={(option) =>
                  option === "All"
                    ? "All Places"
                    : (option.parkingCode ?? option)
                }
                renderOption={(props, option) => (
                  <li {...props}>
                    <div>
                      {option === "All" ? (
                        "All Places"
                      ) : (
                        <>
                          <MDBadge
                            badgeContent={
                              <p>{option.parkingCode.toString()}</p>
                            }
                            color="secondary"
                            variant="gradient"
                            size="sm"
                          />
                          {" | "}
                          {option?.google?.formatted_address}
                        </>
                      )}
                    </div>
                  </li>
                )}
                name="placeIds"
                autoHighlight
                disableClearable
                size="small"
                value={
                  filterOptions.placeId.length === allPlaces.length
                    ? allPlaces
                    : allPlaces.filter((place) =>
                        filterOptions.placeId.includes(place?._id),
                      )
                }
                onChange={handlePlaceChange}
                renderInput={(params) => (
                  <MDInput
                    label="Place"
                    fullWidth
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: "new-password",
                    }}
                    {...params}
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <Autocomplete
                disableClearable
                name="tz"
                value={filterOptions.tz}
                options={timeZones}
                onChange={(event, newValue) => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    tz: newValue,
                  }));
                }}
                freeSolo
                size="small"
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="contained"
                      label={option}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <MDInput
                    label="Select Timezone"
                    fullWidth={true}
                    {...params}
                  />
                )}
              />
            </FormControl>
          </Grid>
          {/* Row 2: Start Date + End Date + Status + Purpose */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <DatePicker
                id="startDate"
                placeholder="Start Date"
                size="md"
                format="MM/dd/yyyy hh:mm a"
                showMeridiem
                ranges={[]}
                value={
                  filterOptions.startDate
                    ? moment(filterOptions.startDate).toDate()
                    : null
                }
                onChange={handleStartDateChange}
                onClean={() => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    startDate: null,
                  }));
                  setStatistics({});
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <DatePicker
                id="endDate"
                placeholder="End Date"
                size="md"
                format="MM/dd/yyyy hh:mm a"
                showMeridiem
                ranges={[]}
                value={
                  filterOptions.endDate
                    ? moment(filterOptions.endDate).toDate()
                    : null
                }
                onChange={handleEndDateChange}
                onClean={() => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    endDate: null,
                  }));
                  setStatistics({});
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <Autocomplete
                disableClearable
                name="type"
                value={filterOptions.status}
                options={["All", "Success", "Failed", "Refunded", "Pending"]}
                onChange={(event, newValue) => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    status: newValue,
                  }));
                }}
                freeSolo
                size="small"
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="contained"
                      label={option}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <MDInput
                    label="Payment Status"
                    fullWidth={true}
                    {...params}
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <Autocomplete
                disableClearable
                name="purpose"
                value={filterOptions.purpose}
                options={["All", "Transient", "Subscription"]}
                onChange={(event, newValue) => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    purpose: newValue,
                  }));
                }}
                freeSolo
                size="small"
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="contained"
                      label={option}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <MDInput label="Purpose" fullWidth={true} {...params} />
                )}
              />
            </FormControl>
          </Grid>
          {/* Row 3: Actions */}
          <Grid item xs={12} md={2}>
            <MDBox display="flex" justifyContent="flex-start" gap={1}>
              <Tooltip title="Get report" arrow>
                <MDButton
                  size="small"
                  color={sidenavColor}
                  disabled={
                    filterOptions.startDate && filterOptions.endDate
                      ? false
                      : true
                  }
                  onClick={handleGetReport}
                >
                  Go
                </MDButton>
              </Tooltip>
              <Tooltip title="Export this report" arrow>
                <MDButton
                  color={sidenavColor}
                  disabled={
                    (totalTransactions === 0 &&
                      (statistics?.isAll
                        ? (statistics?.all?.totalValidations === 0 && statistics?.all?.totalFree === 0)
                        : (statistics?.totalValidations === 0 && statistics?.totalFree === 0))) ||
                    downloading
                  }
                  onClick={downloadReport}
                  size="small"
                  endIcon={<DownloadIcon />}
                >
                  {downloading ? `${downloadProgress}%` : ""}
                </MDButton>
              </Tooltip>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <MDBox>
        {!isEmpty(statistics) ? (
          <>
            <MDBox pt={2}>
              <Statistics data={statistics} />
            </MDBox>
          </>
        ) : (
          <MDBox pt={4}>
            <NoDataOverlay />
          </MDBox>
        )}
      </MDBox>
      <CircularIndeterminate
        type="full"
        size={20}
        text="Please wait..."
        open={loading}
      />
    </>
  );
}

export default TransactionReports;
